import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  Bold, Italic, Underline, Heading1, Heading2, List, ListOrdered, Link2, Download, Loader2, Type,
} from "lucide-react";
import { api } from "../../lib/api";
import { handlePdfError } from "../../lib/pdf";
import { useAuth } from "../../context/AuthContext";
import { opCost, creditLabel, tierName } from "../../lib/pricing";
import { useOnline } from "../../hooks/usePWA";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import BrandingBar from "./BrandingBar";
import ToolIntro from "./ToolIntro";
import PreviewUnlock from "./PreviewUnlock";

const DEMO = `<h1>Project Proposal</h1><p>This document outlines the scope and goals of our upcoming initiative.</p><h2>Objectives</h2><ul><li>Increase output quality</li><li>Reduce turnaround time</li><li>Delight our customers</li></ul><p>Thank you for reviewing this proposal.</p>`;

export default function RichTextTab({ initial }) {
  const editorRef = useRef(null);
  const [title, setTitle] = useState(initial?.title || "My Document");
  const [branding, setBranding] = useState({ watermark: initial?.watermark || "", brand_color: initial?.brand_color || "" });
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();
  const startHtml = initial?.html || DEMO;
  const cost = opCost(user, "richtext");
  const costLabel = creditLabel(cost);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };

  const makeLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  const btn = (Icon, action, testid, title) => (
    <button
      type="button"
      onClick={action}
      title={title}
      data-testid={testid}
      className="h-9 w-9 flex items-center justify-center rounded-lg text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  const generate = async () => {
    const html = DOMPurify.sanitize(editorRef.current?.innerHTML || "");
    if (!html.trim() || html === "<br>") {
      toast.error("Write something first");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/pdf/richtext", { title, html, ...branding });
      setStage(res.data);
    } catch (e) {
      if (handlePdfError(e) === "no-credits") navigate("/buy-credits");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ToolIntro
        Icon={Type}
        title="Rich Text Editor"
        desc="Write and format a document — headings, lists, links and tables — then export a clean, print-ready A4 PDF."
        accepts="What you type"
        tier={tierName("richtext")}
        costLabel={costLabel}
      />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <Input
          data-testid="richtext-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Document title"
          className="max-w-xs bg-white"
        />
        <div className="flex-1" />
        <Button
          data-testid="generate-pdf-button"
          onClick={generate}
          disabled={loading || !online}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Create preview
        </Button>
      </div>

      {stage && <PreviewUnlock stage={stage} onClose={() => setStage(null)} onUnlocked={() => setStage(null)} />}

      <BrandingBar value={branding} onChange={setBranding} />

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden paper-shadow">
        <div className="flex items-center gap-1 border-b border-slate-200 px-3 py-2 bg-slate-50 flex-wrap">
          {btn(Bold, () => exec("bold"), "rt-bold", "Bold")}
          {btn(Italic, () => exec("italic"), "rt-italic", "Italic")}
          {btn(Underline, () => exec("underline"), "rt-underline", "Underline")}
          <div className="w-px h-6 bg-slate-200 mx-1" />
          {btn(Heading1, () => exec("formatBlock", "<h1>"), "rt-h1", "Heading 1")}
          {btn(Heading2, () => exec("formatBlock", "<h2>"), "rt-h2", "Heading 2")}
          <div className="w-px h-6 bg-slate-200 mx-1" />
          {btn(List, () => exec("insertUnorderedList"), "rt-ul", "Bullet list")}
          {btn(ListOrdered, () => exec("insertOrderedList"), "rt-ol", "Numbered list")}
          {btn(Link2, makeLink, "rt-link", "Insert link")}
          <div className="flex-1" />
          <button
            type="button"
            data-testid="rt-load-demo"
            onClick={() => { if (editorRef.current) editorRef.current.innerHTML = DOMPurify.sanitize(DEMO); }}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 px-2"
          >
            Load sample
          </button>
        </div>
        <div className="p-8 sm:p-12 bg-slate-100 flex justify-center">
          <div
            ref={editorRef}
            data-testid="rich-editor-textarea"
            contentEditable
            suppressContentEditableWarning
            data-placeholder="Start typing your document…"
            className="rt-editor bg-white w-full max-w-[640px] min-h-[520px] p-10 rounded-sm paper-shadow text-slate-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(startHtml) }}
          />
        </div>
      </div>
    </div>
  );
}
